import React, { useCallback, useEffect, useState } from 'react';

import { WorkflowEditorPage } from 'pages/WorkflowEditorPage';
import { WorkflowSelectorPage } from 'pages/WorkflowSelectorPage';
import { telemetry } from 'lib/telemetry';
import { normalizeWorkflow, type RawWorkflowDto, type WorkflowDto } from 'model/workflow';

const App = () => {
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowDto | null>(null);

  const handleWorkflowLoaded = useCallback(
    (loadedWorkflowId: string, loadedWorkflow: WorkflowDto) => {
      setWorkflowId(loadedWorkflowId);
      setWorkflow(loadedWorkflow);
    },
    [],
  );

  const handleBackToSelector = useCallback(() => {
    setWorkflowId(null);
    setWorkflow(null);
  }, []);

  // Dev bypass: `?dev=1` skips auth + workflow lookup and drops straight
  // into the editor with a bundled fixture. Tree-shaken out of production
  // builds via `import.meta.env.DEV`, so the fixture never ships and the
  // backdoor is dev-only.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (workflowId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('dev') !== '1') return;
    void import('../workflow_examples/simple/Blur.json').then((mod) => {
      const raw = (mod.default ?? mod) as unknown as RawWorkflowDto;
      handleWorkflowLoaded('dev-stub-blur', normalizeWorkflow(raw));
    });
  }, [handleWorkflowLoaded, workflowId]);

  // Drive page-view telemetry from the page-state machine. When the
  // adapter is unset (dev default) this is a no-op.
  useEffect(() => {
    if (workflow && workflowId) {
      telemetry.trackPageView('Editor', { workflowId });
    } else {
      telemetry.trackPageView('Selector');
    }
  }, [workflow, workflowId]);

  if (workflow && workflowId) {
    return (
      <WorkflowEditorPage
        workflowId={workflowId}
        initialWorkflow={workflow}
        onBack={handleBackToSelector}
      />
    );
  }

  return <WorkflowSelectorPage onWorkflowLoaded={handleWorkflowLoaded} />;
};

export default React.memo(App);
